<div *ngIf="errorMessage" class="bg-red-50 text-red-700 border border-red-200 rounded-md px-4 py-3 mb-4 text-sm">
  <i class="fas fa-exclamation-circle mr-1"></i> {{ errorMessage }}
</div>

<div *ngIf="loading" class="text-center text-gray-500 py-10">
  <i class="fas fa-spinner fa-spin mr-1"></i> Loading retention...
</div>

<div *ngIf="!loading && report">
  <!-- Headline tiles -->
  <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">New customers</div>
      <div class="text-2xl font-bold text-gray-900">{{ report.totals.customers | number }}</div>
      <div class="text-xs text-gray-400">accounts created in this period</div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Had a pickup</div>
      <div class="text-2xl font-bold text-gray-900">{{ report.totals.pickupRate != null ? report.totals.pickupRate + '%' : '—' }}</div>
      <div class="text-xs text-gray-400">{{ report.totals.customersWithPickup | number }} customers</div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Came back</div>
      <div class="text-2xl font-bold text-green-700">{{ report.totals.repeatRate != null ? report.totals.repeatRate + '%' : '—' }}</div>
      <div class="text-xs text-gray-400">of those, had a 2nd pickup</div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Avg pickups</div>
      <div class="text-2xl font-bold text-gray-900">{{ report.totals.averagePickups ?? '—' }}</div>
      <div class="text-xs text-gray-400">per customer with a pickup</div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Revenue / customer</div>
      <div class="text-2xl font-bold text-gray-900">{{ report.totals.revenuePerCustomerWithPickup != null ? (report.totals.revenuePerCustomerWithPickup | currency:'INR':'symbol':'1.0-0') : '—' }}</div>
      <div class="text-xs text-gray-400">with a pickup<ng-container *ngIf="report.totals.revenuePerCustomer != null"> · {{ report.totals.revenuePerCustomer | currency:'INR':'symbol':'1.0-0' }} per sign-up</ng-container></div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">From repeats</div>
      <div class="text-2xl font-bold text-gray-900">{{ repeatRevenueShare(report.totals) != null ? repeatRevenueShare(report.totals) + '%' : '—' }}</div>
      <div class="text-xs text-gray-400">of pickup revenue</div>
    </div>
  </div>

  <div *ngIf="report.rows.length === 0" class="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500 text-sm">
    No customers were created in this period.
  </div>

  <div *ngIf="report.rows.length > 0">
    <div class="bg-white rounded-lg border border-gray-200 p-3 mb-6">
      <app-chart-wrapper [data]="repeatRateChart" title="Repeat rate by channel (%)" type="bar" [colors]="['#16a34a']" [height]="260"></app-chart-wrapper>
    </div>

    <div class="bg-white rounded-lg border border-gray-200 mb-4 overflow-x-auto">
      <table class="min-w-full text-sm">
        <thead class="bg-gray-50 text-xs text-gray-500 uppercase">
          <tr>
            <th class="text-left px-3 py-2">Channel</th>
            <th class="text-right px-3 py-2" title="Accounts created in this period, one per phone number">Customers</th>
            <th class="text-right px-3 py-2">1+ pickup</th>
            <th class="text-right px-3 py-2">Came back (2+)</th>
            <th class="text-right px-3 py-2">3+ pickups</th>
            <th class="text-right px-3 py-2">Avg pickups</th>
            <th class="text-right px-3 py-2">First-pickup revenue</th>
            <th class="text-right px-3 py-2">Repeat revenue</th>
            <th class="text-right px-3 py-2" title="Revenue ÷ customers with at least one pickup">Revenue / customer with a pickup</th>
            <th class="text-right px-3 py-2" title="Revenue ÷ all customers, including those who never had a pickup">Revenue / sign-up</th>
            <th class="text-right px-3 py-2">Days to 1st</th>
            <th class="text-right px-3 py-2">Days 1st → 2nd</th>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let row of report.rows">
            <ng-container *ngTemplateOutlet="metricRow; context: { $implicit: row, label: row.label, total: false }"></ng-container>
          </ng-container>
          <ng-container *ngTemplateOutlet="metricRow; context: { $implicit: report.totals, label: 'Total', total: true }"></ng-container>
        </tbody>
      </table>
    </div>
  </div>

  <p class="text-xs text-gray-400 leading-relaxed">
    Customers whose account was created in this period, grouped by how they first reached us. Pickups are every valid collection up to
    {{ report.asOf | date:'d MMM y' }}, so recent customers have had less time to come back: compare channels within the same period.
    Unattributed customers signed up in the app or were added by staff without any marketing touch. Customers are counted once per
    phone number (accounts without one are left out). This is not the funnel's sign-ups: the funnel follows leads first seen in this
    period, so someone who messaged in September and signed up in October is a sign-up in September's funnel but a new customer here.
    Unattributed counts should match between the two. Revenue / customer counts only customers who had a pickup (compare it with CAC
    per first pickup); revenue / sign-up spreads the same revenue over every new customer, including those we couldn't serve or who never
    booked (compare it with cost per sign-up).
  </p>
</div>

<ng-template #metricRow let-m let-label="label" let-total="total">
  <tr class="border-t" [class.border-gray-100]="!total" [class.border-t-2]="total" [class.border-gray-200]="total"
    [class.bg-gray-50]="total" [class.font-semibold]="total">
    <td class="px-3 py-2 text-gray-900 whitespace-nowrap">{{ label }}</td>
    <td class="px-3 py-2 text-right">{{ m.customers | number }}</td>
    <td class="px-3 py-2 text-right">{{ m.customersWithPickup | number }}<span class="text-xs text-gray-400" *ngIf="m.pickupRate != null"> ({{ m.pickupRate }}%)</span></td>
    <td class="px-3 py-2 text-right">{{ m.customersWithTwoPlusPickups | number }}<span class="text-xs text-gray-400" *ngIf="m.repeatRate != null"> ({{ m.repeatRate }}%)</span></td>
    <td class="px-3 py-2 text-right">{{ m.customersWithThreePlusPickups | number }}<span class="text-xs text-gray-400" *ngIf="m.threePlusRate != null"> ({{ m.threePlusRate }}%)</span></td>
    <td class="px-3 py-2 text-right">{{ m.averagePickups ?? '—' }}</td>
    <td class="px-3 py-2 text-right text-green-700">{{ m.firstPickupRevenue | currency:'INR':'symbol':'1.0-0' }}</td>
    <td class="px-3 py-2 text-right text-green-700">{{ m.repeatRevenue | currency:'INR':'symbol':'1.0-0' }}</td>
    <td class="px-3 py-2 text-right">{{ m.revenuePerCustomerWithPickup != null ? (m.revenuePerCustomerWithPickup | currency:'INR':'symbol':'1.0-0') : '—' }}</td>
    <td class="px-3 py-2 text-right text-gray-500">{{ m.revenuePerCustomer != null ? (m.revenuePerCustomer | currency:'INR':'symbol':'1.0-0') : '—' }}</td>
    <td class="px-3 py-2 text-right">{{ m.medianDaysToFirstPickup ?? '—' }}</td>
    <td class="px-3 py-2 text-right">{{ m.medianDaysToSecondPickup ?? '—' }}</td>
  </tr>
</ng-template>
